import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, Plus, Trash2, CheckCircle2, Circle, Upload, Sparkles, AlertTriangle, ShieldAlert, User as UserIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const UNIT_OPTIONS = ["tablets", "capsules", "ml", "bottles", "packs", "patches", "ampoules", "sachets", "other"];
const CONDITION_OPTIONS = [
  { value: "intact", label: "Intact" },
  { value: "damaged", label: "Damaged" },
  { value: "other", label: "Other" },
];

const emptyItem = () => ({
  client_id: "",
  medication_id: "",
  medication_name: "",
  strength: "",
  form: "",
  is_controlled_drug: false,
  expected_quantity: 1,
  received_quantity: 1,
  unit: "",
  batch_no: "",
  expiry_date: "",
  package_condition: "intact",
  package_condition_note: "",
  discrepancy_reason: "",
  action_taken: "",
  // OCR draft flag — until the operator picks a real medication_id, this row is a
  // suggestion only and cannot be saved.
  ocr_draft_name: "",
  // Per-CD witness auth state
  witness_email: "",
  witness_password: "",
  witness_user_id: "",
  witness_signature_name: "",
  witness_verified: false,
  witness_verifying: false,
});

export default function DeliveryNotes() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ocring, setOcring] = useState(false);
  const [attachingFile, setAttachingFile] = useState(false);
  const [medsByClient, setMedsByClient] = useState({}); // { clientId: [med, ...] }
  const defaultSiteId = user?.site_id || "";
  const [form, setForm] = useState({
    pharmacy_name: "",
    delivery_date: new Date().toISOString().slice(0, 10),
    delivery_time: "",
    note_reference: "",
    site_id: defaultSiteId,
    delivery_note_file_id: "",
    delivery_note_file_name: "",
    items: [emptyItem()],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, c, s] = await Promise.all([
        api.get("/delivery-notes"),
        api.get("/clients"),
        api.get("/sites"),
      ]);
      setNotes(n.data || []);
      setClients(c.data || []);
      setSites((s.data || []).filter((x) => (x.status || "active") !== "archived"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDialog = () => {
    setForm({
      pharmacy_name: "",
      delivery_date: new Date().toISOString().slice(0, 10),
      delivery_time: "",
      note_reference: "",
      site_id: user?.site_id || (sites.length === 1 ? sites[0].id : ""),
      delivery_note_file_id: "",
      delivery_note_file_name: "",
      items: [emptyItem()],
    });
    setOpen(true);
  };

  const loadMedsForClient = useCallback(async (clientId) => {
    if (!clientId || medsByClient[clientId]) return medsByClient[clientId] || [];
    try {
      const r = await api.get(`/clients/${clientId}/sections/medication`);
      const active = (r.data || []).filter((m) => (m.status || "active") === "active");
      setMedsByClient((prev) => ({ ...prev, [clientId]: active }));
      return active;
    } catch (e) {
      toast.error(formatApiError(e));
      return [];
    }
  }, [medsByClient]);

  // Clients visible in the picker are those that belong to the selected site.
  const clientsForSite = useMemo(() => {
    if (!form.site_id) return clients;
    return clients.filter((c) => {
      if (c.site_id === form.site_id) return true;
      return (c.additional_site_ids || []).includes(form.site_id);
    });
  }, [clients, form.site_id]);

  const attachDeliveryPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAttachingFile(true);
    const t = toast.loading("Uploading delivery note…");
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("related_module", "delivery_note");
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({
        ...prev,
        delivery_note_file_id: r.data.id,
        delivery_note_file_name: r.data.original_filename || f.name,
      }));
      toast.success("Delivery note file attached", { id: t });
    } catch (err) { toast.error(formatApiError(err), { id: t }); }
    finally {
      setAttachingFile(false);
      e.target.value = "";
    }
  };

  const runOcr = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) { toast.error("OCR needs a PDF file"); return; }
    setOcring(true);
    const t = toast.loading("Reading pharmacy PDF…");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/delivery-notes/parse-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const extracted = (r.data.items || []).map((it) => ({
        ...emptyItem(),
        ocr_draft_name: it.medication_name || "",
        medication_name: it.medication_name || "",
        expected_quantity: it.expected_quantity || 1,
        received_quantity: it.expected_quantity || 1,
        batch_no: it.batch_no || "",
        expiry_date: it.expiry_date || "",
      }));
      if (extracted.length === 0) {
        toast.error("No line items detected — add them manually", { id: t });
      } else {
        setForm((prev) => ({ ...prev, items: extracted }));
        toast.success(`Extracted ${extracted.length} draft line(s) — pick a service user and medication for each`, { id: t });
      }
    } catch (err) { toast.error(formatApiError(err), { id: t }); }
    finally {
      setOcring(false);
      e.target.value = "";
    }
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));

  const onPickClient = async (i, clientId) => {
    updateItem(i, { client_id: clientId, medication_id: "", medication_name: "", strength: "", form: "", is_controlled_drug: false });
    await loadMedsForClient(clientId);
  };

  const onPickMedication = (i, medId) => {
    const clientId = form.items[i]?.client_id;
    const meds = medsByClient[clientId] || [];
    const med = meds.find((m) => m.id === medId);
    if (!med) return;
    const isCD = med.is_controlled_drug === true || med.is_controlled_drug === "yes";
    updateItem(i, {
      medication_id: med.id,
      medication_name: med.medication_name || "",
      strength: med.dose || "",
      form: med.route || "",
      is_controlled_drug: isCD,
      // Reset witness verification whenever the med changes
      witness_email: "",
      witness_password: "",
      witness_user_id: "",
      witness_signature_name: "",
      witness_verified: false,
    });
  };

  const verifyWitness = async (i) => {
    const it = form.items[i];
    if (!it.witness_email.trim() || !it.witness_password) {
      toast.error("Enter the witness colleague's email and password");
      return;
    }
    if (it.witness_email.trim().toLowerCase() === (user?.email || "").toLowerCase()) {
      toast.error("Witness must be a different person from you");
      return;
    }
    updateItem(i, { witness_verifying: true });
    try {
      const r = await api.post("/emar/verify-witness", { email: it.witness_email.trim(), password: it.witness_password });
      updateItem(i, {
        witness_verified: true,
        witness_user_id: r.data.witness_user_id,
        witness_signature_name: r.data.witness_name,
        witness_password: "",
        witness_verifying: false,
      });
      toast.success(`Witness verified · ${r.data.witness_name}`);
    } catch (e) {
      updateItem(i, { witness_verified: false, witness_user_id: "", witness_verifying: false });
      toast.error(formatApiError(e));
    }
  };

  const submit = async () => {
    if (!form.pharmacy_name.trim() || !form.delivery_date || !form.site_id) {
      toast.error("Pharmacy, delivery date and site are all required");
      return;
    }
    if (form.items.length === 0) {
      toast.error("Add at least one line item");
      return;
    }
    for (let i = 0; i < form.items.length; i++) {
      const it = form.items[i];
      if (!it.client_id) { toast.error(`Line ${i + 1}: pick a service user`); return; }
      if (!it.medication_id) { toast.error(`Line ${i + 1}: pick a medication from ${clients.find((c) => c.id === it.client_id)?.full_name || "the service user"}'s active list`); return; }
      const exp = Number(it.expected_quantity);
      const rec = Number(it.received_quantity);
      if (!(exp >= 0) || !(rec >= 0)) { toast.error(`Line ${i + 1}: expected and received quantities must be numbers ≥ 0`); return; }
      const isDisc = Math.abs(rec - exp) > 1e-6;
      if (isDisc && (!it.discrepancy_reason.trim() || !it.action_taken.trim())) {
        toast.error(`Line ${i + 1}: discrepancy needs a reason AND an action taken`);
        return;
      }
      if (it.is_controlled_drug && rec > 0 && !it.witness_verified) {
        toast.error(`Line ${i + 1}: controlled drug — verify a witness before saving`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        pharmacy_name: form.pharmacy_name.trim(),
        delivery_date: form.delivery_date,
        delivery_time: form.delivery_time || null,
        note_reference: form.note_reference || null,
        site_id: form.site_id,
        delivery_note_file_id: form.delivery_note_file_id || null,
        items: form.items.map((it) => ({
          client_id: it.client_id,
          medication_id: it.medication_id,
          medication_name: it.medication_name,
          strength: it.strength || null,
          form: it.form || null,
          expected_quantity: Number(it.expected_quantity),
          received_quantity: Number(it.received_quantity),
          unit: it.unit || null,
          batch_no: it.batch_no || null,
          expiry_date: it.expiry_date || null,
          package_condition: it.package_condition || null,
          package_condition_note: it.package_condition_note || null,
          discrepancy_reason: it.discrepancy_reason || null,
          action_taken: it.action_taken || null,
          witness_user_id: it.witness_verified ? it.witness_user_id : null,
          witness_signature_name: it.witness_verified ? it.witness_signature_name : null,
        })),
      };
      await api.post("/delivery-notes", payload);
      const cdCount = form.items.filter((it) => it.is_controlled_drug && Number(it.received_quantity) > 0).length;
      toast.success(cdCount > 0
        ? `Delivery note saved · ${cdCount} CD line${cdCount > 1 ? "s" : ""} booked into the CD ledger`
        : "Delivery note saved"
      );
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const deleteNote = async (n) => {
    if (!window.confirm(`Delete delivery note from ${n.pharmacy_name} (${n.delivery_date})?`)) return;
    try {
      await api.delete(`/delivery-notes/${n.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const siteName = (id) => sites.find((s) => s.id === id)?.name || "—";

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="h-14 border-b border-slate-200 bg-white px-6 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Truck className="w-3 h-3 text-sky-700" /> Clinical</div>
          <h1 className="font-heading text-xl font-semibold" data-testid="dn-title">Pharmacy Delivery Notes</h1>
        </div>
        <Button size="sm" onClick={openDialog} className="h-9 bg-slate-900 hover:bg-slate-800" data-testid="btn-new-delivery-note">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Log delivery note
        </Button>
      </div>

      <div className="p-6 space-y-4">
        {loading ? (
          <div className="text-sm text-slate-500" data-testid="dn-loading">Loading delivery notes…</div>
        ) : notes.length === 0 ? (
          <div className="bg-white border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="dn-empty">
            No delivery notes yet. Log the next pharmacy delivery so its expected quantities auto-populate when the CD stock is booked in.
          </div>
        ) : (
          <ul className="space-y-3" data-testid="dn-list">
            {notes.map((n) => {
              const total = n.items?.length || 0;
              const reconciled = (n.items || []).filter((i) => i.reconciled).length;
              const discrepancies = (n.items || []).filter((i) => i.discrepancy).length;
              return (
                <li key={n.id} className="bg-white border border-slate-200 p-4" data-testid={`dn-${n.id}`}>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div className="font-medium text-slate-900">{n.pharmacy_name}</div>
                    <div className="text-xs text-slate-500">{n.delivery_date}{n.delivery_time ? ` · ${n.delivery_time}` : ""}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Site · {siteName(n.site_id)}</div>
                    {n.note_reference && <div className="text-[10px] uppercase tracking-widest text-slate-500">Ref · {n.note_reference}</div>}
                    {n.received_by_name && <div className="text-[10px] uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /> {n.received_by_name}</div>}
                    <div className="ml-auto flex items-center gap-2">
                      {discrepancies > 0 && (
                        <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border bg-amber-50 border-amber-300 text-amber-800 inline-flex items-center gap-1" data-testid={`dn-discrepancies-${n.id}`}>
                          <AlertTriangle className="w-3 h-3" /> {discrepancies} discrepancy
                        </span>
                      )}
                      <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${reconciled === total ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-slate-100 border-slate-300 text-slate-700"}`} data-testid={`dn-status-${n.id}`}>
                        {reconciled}/{total} booked into CD ledger
                      </span>
                      <Button size="sm" variant="outline" className="h-7 text-red-600" onClick={() => deleteNote(n)} data-testid={`btn-dn-delete-${n.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <table className="mt-3 min-w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600 uppercase tracking-widest text-[10px]">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Service user</th>
                        <th className="px-2 py-1.5 text-left">Medication</th>
                        <th className="px-2 py-1.5 text-right">Expected</th>
                        <th className="px-2 py-1.5 text-right">Received</th>
                        <th className="px-2 py-1.5">Unit</th>
                        <th className="px-2 py-1.5">Batch</th>
                        <th className="px-2 py-1.5">Expiry</th>
                        <th className="px-2 py-1.5">Package</th>
                        <th className="px-2 py-1.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {n.items.map((it) => (
                        <tr key={it.item_id} className="border-t border-slate-100">
                          <td className="px-2 py-1.5">{it.client_name || "—"}</td>
                          <td className="px-2 py-1.5">
                            {it.medication_name}
                            {it.strength ? <span className="text-slate-500"> · {it.strength}</span> : null}
                            {it.is_controlled_drug && <span className="ml-1 text-[9px] uppercase tracking-widest px-1 py-0.5 border border-red-300 bg-red-50 text-red-700">CD</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{it.expected_quantity}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            {it.received_quantity ?? "—"}
                            {it.discrepancy && <span className="ml-1 text-amber-700">⚠</span>}
                          </td>
                          <td className="px-2 py-1.5">{it.unit || "—"}</td>
                          <td className="px-2 py-1.5">{it.batch_no || "—"}</td>
                          <td className="px-2 py-1.5">{it.expiry_date || "—"}</td>
                          <td className="px-2 py-1.5 capitalize">{it.package_condition || "—"}</td>
                          <td className="px-2 py-1.5">
                            {it.reconciled ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> booked</span>
                            ) : it.is_controlled_drug ? (
                              <span className="inline-flex items-center gap-1 text-slate-500"><Circle className="w-3 h-3" /> pending</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-slate-500"><CheckCircle2 className="w-3 h-3" /> logged</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-full sm:max-w-4xl max-h-[90dvh] overflow-y-auto" data-testid="dn-dialog">
          <DialogHeader>
            <DialogTitle>Log pharmacy delivery note</DialogTitle>
            <DialogDescription>
              Record medications received from the pharmacy. AYOBELL will compare expected and received quantities and highlight any discrepancies. Controlled drugs require additional verification before stock is updated.
            </DialogDescription>
          </DialogHeader>

          {/* Delivery header */}
          <div className="border border-slate-200 p-3 space-y-3" data-testid="dn-header">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Delivery header</div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="col-span-3">
                <Label>Pharmacy / Supplier <span className="text-red-600">*</span></Label>
                <Input value={form.pharmacy_name} onChange={(e) => setForm({ ...form, pharmacy_name: e.target.value })} placeholder="e.g. Boots Pharmacy" data-testid="dn-pharmacy" />
              </div>
              <div className="col-span-2">
                <Label>Delivery date <span className="text-red-600">*</span></Label>
                <Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} data-testid="dn-date" />
              </div>
              <div>
                <Label>Time</Label>
                <Input type="time" value={form.delivery_time} onChange={(e) => setForm({ ...form, delivery_time: e.target.value })} data-testid="dn-time" />
              </div>
              <div className="col-span-3">
                <Label>Delivery reference</Label>
                <Input value={form.note_reference} onChange={(e) => setForm({ ...form, note_reference: e.target.value })} placeholder="e.g. DN-2026-0142" data-testid="dn-ref" />
              </div>
              <div className="col-span-3">
                <Label>Site / House <span className="text-red-600">*</span></Label>
                <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v, items: form.items.map((it) => ({ ...it, client_id: "", medication_id: "" })) })}>
                  <SelectTrigger className="h-9" data-testid="dn-site"><SelectValue placeholder="Choose site" /></SelectTrigger>
                  <SelectContent>
                    {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-3">
                <Label>Received by</Label>
                <div className="h-9 px-3 border border-slate-200 bg-slate-50 text-sm text-slate-700 flex items-center gap-2" data-testid="dn-received-by">
                  <UserIcon className="w-3.5 h-3.5 text-slate-500" /> {user?.name || user?.email || "—"}
                </div>
              </div>
              <div className="col-span-3">
                <Label>Delivery note file (PDF / image)</Label>
                <label className="h-9 px-3 border border-slate-300 bg-white text-sm text-slate-700 flex items-center gap-2 cursor-pointer hover:bg-slate-50" data-testid="dn-file">
                  <Upload className={`w-3.5 h-3.5 ${attachingFile ? "animate-pulse" : ""}`} />
                  {form.delivery_note_file_name || (attachingFile ? "Uploading…" : "Attach signed delivery note")}
                  <input type="file" accept="application/pdf,image/*" className="hidden" onChange={attachDeliveryPdf} disabled={attachingFile} />
                </label>
              </div>
            </div>
          </div>

          {/* Line items */}
          <div className="border border-slate-200 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Medications received</div>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-1.5 h-7 px-2 border border-sky-300 bg-sky-50 text-sky-800 text-xs cursor-pointer hover:bg-sky-100" data-testid="btn-dn-ocr-pdf">
                  <Upload className={`w-3 h-3 ${ocring ? "animate-pulse" : ""}`} />
                  {ocring ? "Reading…" : "Suggest lines from PDF"}
                  <input type="file" accept="application/pdf" className="hidden" onChange={runOcr} disabled={ocring} />
                </label>
                <Button size="sm" variant="outline" className="h-7" onClick={addItem} data-testid="btn-dn-add-item">
                  <Plus className="w-3 h-3 mr-1" /> Add line
                </Button>
              </div>
            </div>
            {form.items.some((it) => it.ocr_draft_name) && (
              <div className="text-[11px] text-sky-800 bg-sky-50 border border-sky-200 px-2 py-1 inline-flex items-center gap-1" data-testid="dn-ocr-hint">
                <Sparkles className="w-3 h-3" /> Draft lines from PDF — pick a service user &amp; medication for each before saving.
              </div>
            )}

            <ul className="space-y-3">
              {form.items.map((it, i) => {
                const meds = medsByClient[it.client_id] || [];
                const exp = Number(it.expected_quantity);
                const rec = Number(it.received_quantity);
                const discrepancy = it.medication_id && !Number.isNaN(exp) && !Number.isNaN(rec) && Math.abs(rec - exp) > 1e-6;
                return (
                  <li key={i} className="border border-slate-200 p-3 space-y-3" data-testid={`dn-item-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Line {i + 1}</div>
                      <Button size="sm" variant="outline" className="h-7 text-red-600" onClick={() => removeItem(i)} disabled={form.items.length === 1} data-testid={`btn-dn-remove-${i}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                    {it.ocr_draft_name && !it.medication_id && (
                      <div className="text-[11px] text-sky-800 bg-sky-50 border border-sky-200 px-2 py-1" data-testid={`dn-ocr-draft-${i}`}>
                        Draft from PDF: <span className="font-semibold">{it.ocr_draft_name}</span> — confirm the service user and their matching medication record below.
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                      {/* 1. Service user */}
                      <div className="col-span-3">
                        <Label className="text-[10px]">Service user <span className="text-red-600">*</span></Label>
                        <Select value={it.client_id} onValueChange={(v) => onPickClient(i, v)}>
                          <SelectTrigger className="h-9" data-testid={`dn-client-${i}`}><SelectValue placeholder="Choose service user" /></SelectTrigger>
                          <SelectContent>
                            {clientsForSite.length === 0 && <div className="px-2 py-1 text-xs text-slate-500">No service users at this site</div>}
                            {clientsForSite.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {/* 2. Medication (their active meds only) */}
                      <div className="col-span-3">
                        <Label className="text-[10px]">Medication <span className="text-red-600">*</span></Label>
                        <Select value={it.medication_id} onValueChange={(v) => onPickMedication(i, v)} disabled={!it.client_id}>
                          <SelectTrigger className="h-9" data-testid={`dn-med-${i}`}><SelectValue placeholder={it.client_id ? "Choose active medication" : "Pick a service user first"} /></SelectTrigger>
                          <SelectContent>
                            {meds.length === 0 && it.client_id && <div className="px-2 py-1 text-xs text-slate-500">No active medications on record</div>}
                            {meds.map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.medication_name} {m.dose ? `· ${m.dose}` : ""} {m.route ? `· ${m.route}` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {it.medication_id && (
                          <div className="mt-1 text-[11px] text-slate-600 inline-flex items-center gap-1">
                            {it.strength && <span>{it.strength}</span>}
                            {it.form && <span>· {it.form}</span>}
                            {it.is_controlled_drug && (
                              <span className="ml-1 text-[9px] uppercase tracking-widest px-1 py-0.5 border border-red-300 bg-red-50 text-red-700" data-testid={`dn-cd-badge-${i}`}>Controlled drug</span>
                            )}
                          </div>
                        )}
                      </div>
                      {/* 3. Expected qty  4. Received qty  5. Unit */}
                      <div>
                        <Label className="text-[10px]">Expected qty</Label>
                        <Input type="number" step="0.01" value={it.expected_quantity} onChange={(e) => updateItem(i, { expected_quantity: e.target.value })} data-testid={`dn-expected-${i}`} />
                      </div>
                      <div>
                        <Label className="text-[10px]">Received qty</Label>
                        <Input type="number" step="0.01" value={it.received_quantity} onChange={(e) => updateItem(i, { received_quantity: e.target.value })} data-testid={`dn-received-${i}`} />
                      </div>
                      <div>
                        <Label className="text-[10px]">Unit</Label>
                        <Select value={it.unit} onValueChange={(v) => updateItem(i, { unit: v })}>
                          <SelectTrigger className="h-9" data-testid={`dn-unit-${i}`}><SelectValue placeholder="e.g. tablets" /></SelectTrigger>
                          <SelectContent>
                            {UNIT_OPTIONS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {/* 6. Batch  7. Expiry  8. Package condition */}
                      <div>
                        <Label className="text-[10px]">Batch #</Label>
                        <Input value={it.batch_no} onChange={(e) => updateItem(i, { batch_no: e.target.value })} data-testid={`dn-batch-${i}`} />
                      </div>
                      <div>
                        <Label className="text-[10px]">Expiry date</Label>
                        <Input type="date" value={it.expiry_date} onChange={(e) => updateItem(i, { expiry_date: e.target.value })} data-testid={`dn-expiry-${i}`} />
                      </div>
                      <div>
                        <Label className="text-[10px]">Package condition</Label>
                        <Select value={it.package_condition} onValueChange={(v) => updateItem(i, { package_condition: v })}>
                          <SelectTrigger className="h-9" data-testid={`dn-condition-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CONDITION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Match / discrepancy indicator */}
                    {it.medication_id && (
                      discrepancy ? (
                        <div className="border border-amber-300 bg-amber-50 text-amber-900 p-2 text-xs space-y-2" data-testid={`dn-discrepancy-${i}`}>
                          <div className="font-semibold inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Delivery discrepancy — expected {exp}, received {rec}</div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[10px]">Reason / notes <span className="text-red-600">*</span></Label>
                              <Textarea rows={2} value={it.discrepancy_reason} onChange={(e) => updateItem(i, { discrepancy_reason: e.target.value })} placeholder="e.g. Short-shipped by pharmacy" data-testid={`dn-discrepancy-reason-${i}`} />
                            </div>
                            <div>
                              <Label className="text-[10px]">Action taken <span className="text-red-600">*</span></Label>
                              <Textarea rows={2} value={it.action_taken} onChange={(e) => updateItem(i, { action_taken: e.target.value })} placeholder="e.g. Called pharmacy — top-up on next delivery" data-testid={`dn-action-taken-${i}`} />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-emerald-800 inline-flex items-center gap-1" data-testid={`dn-match-${i}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" /> Quantity matched
                        </div>
                      )
                    )}

                    {/* Package damaged / other note */}
                    {["damaged", "other"].includes(it.package_condition) && (
                      <div>
                        <Label className="text-[10px]">Package condition note</Label>
                        <Input value={it.package_condition_note} onChange={(e) => updateItem(i, { package_condition_note: e.target.value })} placeholder="Describe the packaging issue" data-testid={`dn-condition-note-${i}`} />
                      </div>
                    )}

                    {/* CD block */}
                    {it.is_controlled_drug && Number(it.received_quantity) > 0 && (
                      <div className="border border-red-300 bg-red-50 p-3 space-y-2" data-testid={`dn-cd-block-${i}`}>
                        <div className="text-xs font-semibold text-red-800 inline-flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> Controlled drug — additional verification required</div>
                        <div className="text-[11px] text-red-800">A second authorised colleague must sign in below as your witness. Stock will only be added to the CD ledger once this verification succeeds.</div>
                        {it.witness_verified ? (
                          <div className="bg-emerald-50 border border-emerald-300 text-emerald-900 p-2 text-xs flex items-center justify-between" data-testid={`dn-witness-verified-${i}`}>
                            <div>
                              <div className="font-semibold">{it.witness_signature_name}</div>
                              <div className="text-[11px] text-emerald-700">Verified — CD receipt will post to the ledger on save</div>
                            </div>
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateItem(i, { witness_verified: false, witness_user_id: "", witness_password: "" })} data-testid={`btn-dn-witness-change-${i}`}>Change</Button>
                          </div>
                        ) : (
                          <div className="grid grid-cols-3 gap-2 items-end">
                            <Input value={it.witness_email} onChange={(e) => updateItem(i, { witness_email: e.target.value })} placeholder="Witness email" type="email" autoComplete="off" data-testid={`dn-witness-email-${i}`} />
                            <Input value={it.witness_password} onChange={(e) => updateItem(i, { witness_password: e.target.value })} placeholder="Witness password" type="password" autoComplete="off" data-testid={`dn-witness-password-${i}`} />
                            <Button size="sm" variant="outline" onClick={() => verifyWitness(i)} disabled={it.witness_verifying} data-testid={`btn-dn-verify-witness-${i}`} className="h-9">
                              {it.witness_verifying ? "Verifying…" : "Verify witness"}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-dn-save">
              {saving ? "Saving…" : "Save delivery note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
